import { useMemo } from 'react';
import { Chain, ChainID } from '@ankr.com/chains-list';

import {
  tendermintRpcChains,
  getChainGroups,
  kavaTendermintRpcChains,
  tacTendermintRpcChains,
} from 'modules/endpoints/constants/groups';
import { ChainGroupID, EndpointGroup } from 'modules/endpoints/types';
import { getGroupedEndpoints } from 'modules/endpoints/utils/getGroupedEndpoints';
import { hasWsFeature } from 'domains/projects/utils/hasWsFeature';
import { selectConfiguredBlockchainsForToken } from 'modules/chains/store/selectors';
import { useProjectFromParams } from 'domains/projects/hooks/useProjectFromParams';
import { useAppSelector } from 'store/useAppSelector';

const isNotTendermintGroup = (group: EndpointGroup) => {
  const firstChainId = group.chains[0].id;

  const isKavaTendermint = kavaTendermintRpcChains.includes(firstChainId);
  const isOtherTendermint = tendermintRpcChains.includes(firstChainId);

  return !isOtherTendermint && !isKavaTendermint;
};

const isNotTonRpcGroup = (group: EndpointGroup) => {
  const firstChainId = group.chains[0].id;

  return firstChainId !== ChainID.TON_RPC;
};

// a vRPC endpoint comes with its parent's grant, so it gets no checkbox of its own
const isNotVrpcGroup = (group: EndpointGroup) => group.id !== ChainGroupID.VRPC;

const getFirstChain = (group: EndpointGroup) => group.chains[0];

const getFirstChainWithPaths = (group: EndpointGroup) => {
  const groupWithPaths = group.chains.find(
    chain => chain.paths && chain.paths.length > 0,
  );

  return groupWithPaths || group.chains[0];
};

const getAllChains = (group: EndpointGroup) => group.chains;

export const mapProjectChains = (chain: Chain, isPathRelative?: boolean) => {
  const {
    beacons: beaconsMainnet,
    id,
    opnodes: opnodesMainnet,
    testnets: chainTestnets,
  } = chain;

  const isFlare = chain.id === ChainID.FLARE;
  const isTac = chain.id === ChainID.TAC;
  const isZkVerify = chain.id === ChainID.ZKVERIFY;
  const isPolkadot = chain.id === ChainID.POLKADOT;

  const endpoints = getGroupedEndpoints({
    chain,
    groups: getChainGroups(),
    shouldExpandTestnets: isFlare || isTac || isZkVerify,
    shouldExpandMainnets: isZkVerify,
  });

  const testnets = chainTestnets?.flatMap(testnet => {
    const { extensions: testnetExtensions = [] } = testnet;

    if (testnetExtensions.length > 0) {
      return [...testnetExtensions];
    }

    return testnet;
  });

  const testnetsWithBeacons = testnets?.filter(({ beacons }) =>
    Boolean(beacons),
  );

  const testnetsWithOpnodes = testnets?.filter(({ opnodes }) =>
    Boolean(opnodes),
  );

  const beaconsTestnetWithFallback =
    testnetsWithBeacons?.flatMap(({ beacons }) => beacons || []) || [];

  const opnodesTestnetWithFallback =
    testnetsWithOpnodes?.flatMap(({ opnodes }) => opnodes || []) || [];

  const beaconsTestnet =
    beaconsTestnetWithFallback?.length > 0
      ? beaconsTestnetWithFallback
      : undefined;

  const opnodesTestnet =
    opnodesTestnetWithFallback?.length > 0
      ? opnodesTestnetWithFallback
      : undefined;

  const chainParams = {
    ...chain,
    mainnets: endpoints.mainnet
      .filter(isNotTendermintGroup)
      .filter(isNotTonRpcGroup)
      .filter(isNotVrpcGroup)
      .map(isPathRelative ? getFirstChainWithPaths : getFirstChain),
    devnets: endpoints.devnet
      .filter(isNotTendermintGroup)
      .filter(isNotTonRpcGroup)
      .map(isPathRelative ? getFirstChainWithPaths : getFirstChain),
    testnets: endpoints.testnet
      .filter(isNotTendermintGroup)
      .filter(isNotTonRpcGroup)
      .map(isPathRelative ? getFirstChainWithPaths : getFirstChain),
    signets: endpoints.signet
      .filter(isNotTendermintGroup)
      .filter(isNotTonRpcGroup)
      .map(isPathRelative ? getFirstChainWithPaths : getFirstChain),
    hasWSFeature: hasWsFeature(chain),
    beaconsMainnet,
    beaconsTestnet,
    opnodesMainnet,
    opnodesTestnet,
  };

  if (isFlare) {
    return {
      ...chainParams,
      testnets: endpoints.testnet
        .filter(isNotTendermintGroup)
        .filter(isNotTonRpcGroup)
        .map(getAllChains)
        .flat(),
    };
  }

  if (chain.id === ChainID.SUI) {
    return {
      ...chainParams,
      mainnets: endpoints.mainnet
        .filter(isNotVrpcGroup)
        .map(getAllChains)
        .flat(),
      testnets: endpoints.testnet.map(getAllChains).flat(),
    };
  }

  if (isZkVerify || isPolkadot) {
    return {
      ...chainParams,
      testnets: endpoints.testnet
        .filter(isNotTendermintGroup)
        .filter(isNotTonRpcGroup)
        .map(getAllChains)
        .flat(),
      mainnets: endpoints.mainnet
        .filter(isNotTendermintGroup)
        .filter(isNotTonRpcGroup)
        .filter(isNotVrpcGroup)
        .map(getAllChains)
        .flat(),
    };
  }

  if (id !== ChainID.SECRET && id !== ChainID.ZETACHAIN && id !== ChainID.SEI) {
    return {
      ...chainParams,
      // JSON-RPC and REST Tendermint subchains have the same path,
      // so should we ignore JSON-RPC endpoints and show REST
      extensions: chain.extensions?.filter(
        extension =>
          !tendermintRpcChains.includes(extension.id) &&
          !kavaTendermintRpcChains.includes(extension.id) &&
          !tacTendermintRpcChains.includes(extension.id),
      ),
    };
  }

  return chainParams;
};

export const useProjectChains = (
  isPathRelative?: boolean,
  nestedEndpointToken?: string,
) => {
  const { userEndpointToken } = useProjectFromParams({ skipFetching: true });

  const chains = useAppSelector(state =>
    selectConfiguredBlockchainsForToken(
      state,
      nestedEndpointToken || userEndpointToken,
    ),
  );

  const projectChains = useMemo(
    () =>
      chains
        .map(chain => mapProjectChains(chain, isPathRelative))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [chains, isPathRelative],
  );

  return { projectChains };
};
